/**
 * API client functions for inventory operations (Phase 1a).
 */

import { API_BASE_URL } from '../config/api';
import type {
  InventoryItem,
  InventoryLocation,
  InventoryLot,
  InventoryValuationReport,
  InventoryCogsReport,
  InventoryTransactionRow,
  InventoryDashboardMetrics,
} from '../types/inventory';

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

export async function getInventoryLocations(
  practiceId: string,
  includeInactive: boolean = false
): Promise<InventoryLocation[]> {
  const params = new URLSearchParams();
  if (includeInactive) params.append('include_inactive', 'true');

  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/inventory/locations?${params.toString()}`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch locations' }));
    throw new Error(error.detail || 'Failed to fetch locations');
  }
  return response.json();
}

export async function getInventoryLots(
  practiceId: string,
  opts?: {
    catalog_item_id?: string;
    catalog_type?: string;
    location_id?: string;
    include_consumed?: boolean;
  }
): Promise<InventoryLot[]> {
  const params = new URLSearchParams();
  if (opts?.catalog_item_id) params.append('catalog_item_id', opts.catalog_item_id);
  if (opts?.catalog_type) params.append('catalog_type', opts.catalog_type);
  if (opts?.location_id) params.append('location_id', opts.location_id);
  if (opts?.include_consumed) params.append('include_consumed', 'true');

  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/inventory/lots?${params.toString()}`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch lots' }));
    throw new Error(error.detail || 'Failed to fetch lots');
  }
  return response.json();
}

export async function getExpiringLots(
  practiceId: string,
  days: number = 60
): Promise<InventoryLot[]> {
  const params = new URLSearchParams();
  params.append('days', String(days));

  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/inventory/lots/expiring?${params.toString()}`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch expiring lots' }));
    throw new Error(error.detail || 'Failed to fetch expiring lots');
  }
  return response.json();
}

export async function getInventoryItems(practiceId: string): Promise<InventoryItem[]> {
  const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}/inventory/items`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch inventory items' }));
    throw new Error(error.detail || 'Failed to fetch inventory items');
  }
  return response.json();
}

export async function getInventoryItemsManage(
  practiceId: string,
  opts?: { include_inactive?: boolean }
): Promise<InventoryItem[]> {
  const params = new URLSearchParams();
  if (opts?.include_inactive) params.append('include_inactive', 'true');

  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/inventory/items/manage?${params.toString()}`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch inventory items' }));
    throw new Error(error.detail || 'Failed to fetch inventory items');
  }
  return response.json();
}

export async function setInventoryItemStockTracked(
  practiceId: string,
  catalogType: string,
  catalogItemId: string,
  isStockTracked: boolean
): Promise<{
  catalog_type: string;
  catalog_item_id: string;
  effective_catalog_item_id: string;
  created_override: boolean;
  is_stock_tracked: boolean;
}> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/inventory/items/${catalogType}/${catalogItemId}/set-stock-tracked`,
    { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ is_stock_tracked: isStockTracked }) }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update stock tracking' }));
    throw new Error(error.detail || 'Failed to update stock tracking');
  }
  return response.json();
}

export async function getInventoryTransactions(
  practiceId: string,
  opts?: {
    start_date?: string;
    end_date?: string;
    source_type?: string;
    catalog_type?: string;
    catalog_item_id?: string;
    location_id?: string;
  }
): Promise<InventoryTransactionRow[]> {
  const params = new URLSearchParams();
  if (opts?.start_date) params.append('start_date', opts.start_date);
  if (opts?.end_date) params.append('end_date', opts.end_date);
  if (opts?.source_type) params.append('source_type', opts.source_type);
  if (opts?.catalog_type) params.append('catalog_type', opts.catalog_type);
  if (opts?.catalog_item_id) params.append('catalog_item_id', opts.catalog_item_id);
  if (opts?.location_id) params.append('location_id', opts.location_id);

  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/inventory/transactions?${params.toString()}`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch inventory transactions' }));
    throw new Error(error.detail || 'Failed to fetch inventory transactions');
  }
  return response.json();
}

export async function setInventoryOnHand(
  practiceId: string,
  catalogType: string,
  catalogItemId: string,
  payload: { desired_quantity: number; unit_cost_received?: string | null; notes?: string | null }
): Promise<{
  catalog_type: string;
  catalog_item_id: string;
  location_id: string;
  previous_on_hand: number;
  new_on_hand: number;
  delta: number;
  created_adjustment_lot_id?: string | null;
}> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/inventory/items/${catalogType}/${catalogItemId}/set-on-hand`,
    { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(payload) }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update on-hand' }));
    throw new Error(error.detail || 'Failed to update on-hand');
  }
  return response.json();
}

export async function getInventoryValuationReport(practiceId: string): Promise<InventoryValuationReport> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/inventory/reports/valuation`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch valuation report' }));
    throw new Error(error.detail || 'Failed to fetch valuation report');
  }
  return response.json();
}

export async function getInventoryCogsReport(
  practiceId: string,
  opts?: { start_date?: string; end_date?: string }
): Promise<InventoryCogsReport> {
  const params = new URLSearchParams();
  if (opts?.start_date) params.append('start_date', opts.start_date);
  if (opts?.end_date) params.append('end_date', opts.end_date);

  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/inventory/reports/cogs?${params.toString()}`,
    { method: 'GET', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch COGS report' }));
    throw new Error(error.detail || 'Failed to fetch COGS report');
  }
  return response.json();
}

export async function getInventoryDashboardMetrics(practiceId: string): Promise<InventoryDashboardMetrics> {
  const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}/inventory/dashboard`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to fetch inventory dashboard' }));
    throw new Error(error.detail || 'Failed to fetch inventory dashboard');
  }
  return response.json();
}

/**
 * Update an existing inventory lot (inline editing).
 */
export async function updateInventoryLot(
  practiceId: string,
  lotId: string,
  payload: {
    lot_number?: string;
    notes?: string;
    expiration_date?: string;
    unit_cost_received?: string;
    quantity_received?: number;
  }
): Promise<InventoryLot> {
  // Only include fields that are explicitly provided
  const body: Record<string, unknown> = {};
  
  if (payload.lot_number !== undefined) {
    body.lot_number = payload.lot_number;
  }
  if (payload.notes !== undefined) {
    // Allow empty string to clear notes
    body.notes = payload.notes || null;
  }
  if (payload.expiration_date !== undefined) {
    body.expiration_date = payload.expiration_date;
  }
  if (payload.unit_cost_received !== undefined) {
    body.unit_cost_received = payload.unit_cost_received;
  }
  if (payload.quantity_received !== undefined) {
    body.quantity_received = payload.quantity_received;
  }

  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/inventory/lots/${lotId}`,
    { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(body) }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update lot' }));
    throw new Error(error.detail || 'Failed to update lot');
  }
  return response.json();
}

/**
 * Delete an inventory lot.
 */
export async function deleteInventoryLot(
  practiceId: string,
  lotId: string
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/inventory/lots/${lotId}`,
    { method: 'DELETE', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete lot' }));
    throw new Error(error.detail || 'Failed to delete lot');
  }
}

/**
 * Update an inventory consumption (transaction).
 * Used to change source_type, notes, or quantity.
 */
export async function updateInventoryConsumption(
  practiceId: string,
  consumptionId: string,
  payload: {
    source_type?: string;
    notes?: string;
    quantity?: number;
  }
): Promise<{
  id: string;
  source_type: string;
  notes: string | null;
  quantity: number;
}> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/inventory/consumptions/${consumptionId}`,
    { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(payload) }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to update transaction' }));
    throw new Error(error.detail || 'Failed to update transaction');
  }
  return response.json();
}

/**
 * Delete an inventory consumption (transaction).
 */
export async function deleteInventoryConsumption(
  practiceId: string,
  consumptionId: string
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/v1/practices/${practiceId}/inventory/consumptions/${consumptionId}`,
    { method: 'DELETE', headers: getAuthHeaders() }
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: 'Failed to delete transaction' }));
    throw new Error(error.detail || 'Failed to delete transaction');
  }
}

/**
 * Adjust inventory by a delta (positive to add, negative to remove).
 * This is a user-friendly wrapper around setInventoryOnHand that lets users
 * think in terms of "add 10 units" rather than "set to 22 units".
 */
export async function adjustInventoryDelta(
  practiceId: string,
  catalogType: string,
  catalogItemId: string,
  currentOnHand: number,
  payload: { delta: number; unit_cost_received?: string | null; notes?: string | null }
): Promise<{
  catalog_type: string;
  catalog_item_id: string;
  location_id: string;
  previous_on_hand: number;
  new_on_hand: number;
  delta: number;
  created_adjustment_lot_id?: string | null;
}> {
  const desiredQuantity = currentOnHand + payload.delta;
  if (desiredQuantity < 0) {
    throw new Error(`Cannot adjust by ${payload.delta}. Current on-hand is ${currentOnHand}.`);
  }
  return setInventoryOnHand(practiceId, catalogType, catalogItemId, {
    desired_quantity: desiredQuantity,
    unit_cost_received: payload.delta > 0 ? payload.unit_cost_received : null,
    notes: payload.notes,
  });
}


